import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import {baseName, childPath, moveTargets, parentOf} from '../../utils/treeEntryNames';
import './TreeDialogs.css';

/**
 * "Move to…" of a file or a folder in the Code tree: pick the folder it goes into (filter by typing), then Move. The
 * folders come from the tree, then from `fetchFolders()` (every folder of the service, not only the ones opened); the
 * item itself and what is inside it are never offered, a folder that already has an entry with its name is refused
 * with the reason. `onMove(targetFolder)` returns a promise; while it runs the buttons are off.
 */
const TreeMoveDialog = ({item, folders, fetchFolders = null, rootLabel = '', isTaken, onMove, onClose}) => {
  const [known, setKnown] = useState(folders);
  const [filter, setFilter] = useState('');
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const here = parentOf(item.path);
  const name = baseName(item.path);

  useEffect(() => {
    if (!fetchFolders) { return undefined; }
    let cancelled = false;
    fetchFolders()
      .then((more) => { if (!cancelled && more?.length) { setKnown((previous) => [...new Set([...previous, ...more])]); } })
      .catch(() => { /* the folders of the tree stay offered */ });
    return () => { cancelled = true; };
  }, [fetchFolders]);

  const targets = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return moveTargets(known, item.path).filter((folder) => !needle || (folder || rootLabel || '/').toLowerCase().includes(needle));
  }, [known, item.path, filter, rootLabel]);

  const labelOf = (folder) => (folder ? (rootLabel ? `${rootLabel}/${folder}` : folder) : (rootLabel || '/ (root)'));
  const clash = choice !== null && choice !== here && isTaken(childPath(choice, name));
  const canMove = choice !== null && choice !== here && !clash && !busy;

  const move = async () => {
    if (!canMove) { return; }
    setBusy(true);
    setError(null);
    try {
      await onMove(choice);
    } catch (failed) {
      setError(failed?.message || 'It could not be moved.');
      setBusy(false);
    }
  };

  return (
    <Modal className="tree-dialog-modal" isOpen onClose={busy ? null : onClose} title={`Move “${name}”`} type="custom">
      <div className="tree-dialog" data-testid="tree-move-dialog">
        <label className="tree-dialog__label" htmlFor="tree-move-filter">Move into</label>
        <input
          autoFocus
          className="tree-dialog__input"
          id="tree-move-filter"
          onChange={(event) => setFilter(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !busy) { onClose(); }
            if (event.key === 'Enter') { move(); }
          }}
          placeholder="Filter folders"
          type="search"
          value={filter}
        />
        <div aria-label="Folders" className="tree-dialog__folders" role="radiogroup">
          {targets.map((folder) => (
            <button
              aria-checked={choice === folder}
              className={`tree-dialog__folder${choice === folder ? ' is-on' : ''}${folder === here ? ' is-here' : ''}`}
              data-testid={`tree-move-target-${folder || 'root'}`}
              disabled={folder === here || busy}
              key={folder || '/'}
              onClick={() => { setChoice(folder); setError(null); }}
              role="radio"
              type="button"
            >
              <span className="tree-dialog__folder-name">{labelOf(folder)}</span>
              {folder === here && <span className="tree-dialog__hint">here now</span>}
            </button>
          ))}
          {targets.length === 0 && <p className="tree-dialog__empty">No folder matches “{filter}”</p>}
        </div>
        {clash && <p className="tree-dialog__error" role="alert">{`“${name}” is already in ${labelOf(choice)}. Rename one of them first.`}</p>}
        {error && <p className="tree-dialog__error" role="alert">{error}</p>}
        <div className="tree-dialog__actions">
          <button className="tree-dialog__btn" disabled={busy} onClick={onClose} type="button">Cancel</button>
          <button className="tree-dialog__btn tree-dialog__btn--primary" data-testid="tree-move-confirm" disabled={!canMove} onClick={move} type="button">
            {busy ? 'Moving…' : 'Move'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

TreeMoveDialog.propTypes = {
  fetchFolders: PropTypes.func,
  folders: PropTypes.arrayOf(PropTypes.string).isRequired,
  isTaken: PropTypes.func.isRequired,
  item: PropTypes.shape({path: PropTypes.string.isRequired, type: PropTypes.string}).isRequired,
  onClose: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  rootLabel: PropTypes.string,
};

export default TreeMoveDialog;
